import type { GanttDataTask, GanttDataTaskRequest } from '@app/services/gantt.service';
import { GANTT_OPTION_OVERRIDES, GanttOptions, GanttAutoSchedulingOptions, GanttColumn, GanttConfigOptions, GanttEditableOptions, GanttResourceOptions, GanttSaveOptions, GanttToolbarOptions, GanttZoomOptions, PartialGanttOptions } from './gantt.types';
import { createDefaultGanttOptions, getGanttOptionOverrides } from './gantt-adapter';

/** Builds page overrides; unspecified options continue to come from colschema. */
export class Gantt implements GanttOptions {
    public rememberId?: string;
    public dataTask?: GanttDataTask;
    public columns: GanttColumn[];
    public config: GanttConfigOptions;
    public editing: GanttEditableOptions;
    public resources: GanttResourceOptions;
    public autoScheduling: GanttAutoSchedulingOptions;
    public zoom: GanttZoomOptions;
    public save: GanttSaveOptions;
    public toolbar: GanttToolbarOptions;

    private readonly configured: PartialGanttOptions = {};

    constructor(params?: PartialGanttOptions) {
        const defaults = createDefaultGanttOptions();
        this.columns = defaults.columns;
        this.config = defaults.config;
        this.editing = defaults.editing;
        this.resources = defaults.resources;
        this.autoScheduling = defaults.autoScheduling;
        this.zoom = defaults.zoom;
        this.save = defaults.save;
        this.toolbar = defaults.toolbar;
        this.setOptions(getGanttOptionOverrides(params) ?? {});
    }

    /** Apply page overrides without losing defaults or earlier builder settings. */
    public setOptions(params: PartialGanttOptions): this {
        params = getGanttOptionOverrides(params) ?? {};
        if (params?.rememberId !== undefined) this.setRememberId(params.rememberId);
        if (params?.dataTask) this.setDataTaskConfig(params.dataTask);
        if (params?.columns) this.setColumns(params.columns);
        if (params?.config) this.setConfig(params.config);
        if (params?.editing) this.setEditing(params.editing);
        if (params?.resources) this.setResources(params.resources);
        if (params?.autoScheduling) this.setAutoScheduling(params.autoScheduling);
        if (params?.zoom) this.setZoom(params.zoom);
        if (params?.save) this.setSaveOptions(params.save);
        if (params?.toolbar) this.setToolbar(params.toolbar);
        return this;
    }

    public setRememberId(id: string): this {
        this.configured.rememberId = id;
        this.rememberId = id;
        return this;
    }

    /** Configure only the provider; generic columns, data, navigation and save are automatic. */
    public setLoadDataFunction(params: GanttDataTaskRequest): this {
        this.configured.dataTask = { loadData: params };
        this.dataTask = { ...this.dataTask, loadData: {
            ...this.dataTask?.loadData,
            ...params,
            parameters: params.parameters === undefined ? this.dataTask?.loadData.parameters
                : typeof params.parameters === 'function' ? params.parameters : { ...params.parameters },
        } };
        return this;
    }

    public setDataTaskConfig(params: GanttDataTask): this {
        return this.setLoadDataFunction(params.loadData);
    }

    public setColumns(columns: GanttColumn[]): this {
        this.configured.columns = columns;
        this.columns = columns.map(column => ({ ...column }));
        return this;
    }

    public addColumn(column: GanttColumn): this {
        this.columns = [...this.columns, { ...column }];
        this.configured.columns = this.columns;
        return this;
    }

    public setConfig(params: GanttConfigOptions): this {
        this.configured.config = { ...this.configured.config, ...params };
        this.config = { ...this.config, ...params };
        return this;
    }

    public setToolbar(params: Partial<GanttToolbarOptions>): this {
        this.configured.toolbar = { ...this.configured.toolbar, ...params };
        this.toolbar = { ...this.toolbar, ...params };
        return this;
    }

    public setSaveOptions(params: Partial<GanttSaveOptions>): this {
        this.configured.save = { ...this.configured.save, ...params };
        this.save = { ...this.save, ...params };
        return this;
    }

    public setEditing(params: Partial<GanttEditableOptions>): this {
        this.configured.editing = { ...this.configured.editing, ...params,
            ...(params.drag ? { drag: { ...this.configured.editing?.drag, ...params.drag } } : {}),
        };
        this.editing = { ...this.editing, ...params,
            ...(this.editing?.drag || params.drag ? { drag: { ...this.editing?.drag, ...params.drag } } : {}),
        };
        return this;
    }

    public setResources(params: Partial<GanttResourceOptions>): this {
        this.configured.resources = { ...this.configured.resources, ...params };
        this.resources = { ...this.resources, ...params };
        if (params.columns) this.setResourceColumns(params.columns);
        return this;
    }

    public setResourceColumns(columns: GanttColumn[]): this {
        this.configured.resources = { ...this.configured.resources, columns };
        this.resources = { ...this.resources, columns: columns.map(column => ({ ...column })) };
        return this;
    }

    public setAutoScheduling(params: Partial<GanttAutoSchedulingOptions>): this {
        this.configured.autoScheduling = { ...this.configured.autoScheduling, ...params };
        this.autoScheduling = { ...this.autoScheduling, ...params };
        return this;
    }

    public setZoom(params: Partial<GanttZoomOptions>): this {
        this.configured.zoom = { ...this.configured.zoom, ...params };
        this.zoom = { ...this.zoom, ...params };
        if (params.levels) this.zoom.levels = [...params.levels];
        return this;
    }

    /** The adapter applies explicit overrides while leaving colschema defaults authoritative. */
    public [GANTT_OPTION_OVERRIDES](): PartialGanttOptions {
        const defaults = createDefaultGanttOptions();
        const values: PartialGanttOptions = {
            rememberId: this.rememberId, dataTask: this.dataTask, columns: this.columns,
            config: this.config, editing: this.editing, resources: this.resources,
            autoScheduling: this.autoScheduling, zoom: this.zoom, save: this.save, toolbar: this.toolbar,
        };
        return pickOverrides(defaults, values, this.configured) as PartialGanttOptions;
    }

    /** Like Grid.build(), returns this configuration instance for ttOptions. */
    public build(): GanttOptions {
        return this;
    }
}

/** Retain explicit defaults as overrides, and also support direct edits to model properties. */
function pickOverrides(defaults: unknown, current: unknown, configured: unknown): unknown {
    if (current === undefined) return undefined;
    if (current !== null && typeof current === 'object' && !Array.isArray(current) && !(current instanceof Date)) {
        const result: Record<string, unknown> = {};
        const original = (defaults ?? {}) as Record<string, unknown>;
        const explicit = (configured ?? {}) as Record<string, unknown>;
        for (const [key, value] of Object.entries(current)) {
            const override = pickOverrides(original[key], value, explicit[key]);
            if (override !== undefined) result[key] = override;
        }
        return result;
    }
    if (configured !== undefined || !sameOption(defaults, current)) return current;
    return undefined;
}

function sameOption(first: unknown, second: unknown): boolean {
    if (first === second) return true;
    if (first instanceof Date && second instanceof Date) return first.getTime() === second.getTime();
    if (!first || !second || typeof first !== 'object' || typeof second !== 'object') return false;
    const firstValues = Object.entries(first);
    const secondValues = Object.entries(second);
    return firstValues.length === secondValues.length && firstValues.every(([key, value]) =>
        Object.prototype.hasOwnProperty.call(second, key) && sameOption(value, (second as Record<string, unknown>)[key]));
}
